/**
 * Per-module calibration and error report for the SAFECARE simulator.
 * Documents sources, assumed vs empirical values, and error metrics per module.
 * Used in Diagnostics and Telemetry JSON export for scientific transparency.
 */

import { getBacktestComparisons } from './historicalBenchmarks.js';

export const CALIBRATION_MODULES = [
    'revenue',
    'spending',
    'enrollment',
    'drug_pricing',
    'admin',
    'trust_fund',
    'compounding',
];

/**
 * Per-module calibration entry: source, assumed value, empirical/target, error metric, citation.
 */
const MODULE_CALIBRATION = {
    revenue: {
        label: 'Revenue (employer/individual/surtax, compliance)',
        sources: [
            'IRS Tax Gap Estimates (2020-2021), voluntary compliance ~85%',
            'CBO revenue scoring conventions; JCT for payroll/surtax',
        ],
        assumed: {
            taxEvasionRate: '12% (slightly more optimistic than IRS gap)',
            wageCoverage: '100% of wages subject when taxEvasionRate applied',
            surtaxAvoidance: '35% (high-income behavioral response)',
        },
        empirical: {
            taxGapPct: '~15% (IRS); model uses 12%',
            note: 'No formal backtest on revenue vs historical single-payer; structure follows bill.',
        },
        errorMetric: 'N/A (no historical single-payer revenue series)',
        citation: 'IRS Tax Gap; CBO/JCT methodology',
    },
    spending: {
        label: 'Spending (NHE, Medicare-based unit costs, utilization)',
        sources: [
            'CMS Medicare Trustees Report (cost per beneficiary, growth ~5.1%)',
            'MedicareBaseline.js age-cost factors (Society of Actuaries, CMS)',
            'NHE baseline from nhe_baseline.json (CMS NHE Accounts)',
        ],
        assumed: {
            costGrowth: '~5.1% (Medicare Trustees extrapolation)',
            utilizationSurge: '15% for newly insured (Years 1-3)',
            ageCostFactors: '0.25–1.0 by age band (Medicare-relative)',
        },
        empirical: {
            medicareCostGrowth: '~5.2% (2015-2024 Trustees historical)',
            utilizationNote: 'ACA Medicaid expansion uptake used as proxy; no direct MAPE.',
        },
        errorMetric: 'Medicare cost growth: ~2% relative error vs historical',
        citation: 'CMS Medicare Trustees Report Table II.D1; MedPAC; NHE.',
    },
    enrollment: {
        label: 'Enrollment (phase-in, take-up)',
        sources: [
            'Bill Sec 1005 phase-in (72%, 85%, 98%, 100%)',
            'ACA Medicaid expansion take-up (KFF, CMS)',
        ],
        assumed: {
            phase1: '72%',
            phase4: '100%',
        },
        empirical: {
            acaTakeup: '~60% Y1, ~85% by Y3 (state variation)',
        },
        errorMetric: 'N/A (bill-defined schedule; no fit to historical)',
        citation: 'Bill Sec 1005; KFF Medicaid expansion data.',
    },
    drug_pricing: {
        label: 'Drug pricing (Sec 405 IRP/DVB, international anchor)',
        sources: [
            'OECD Health Statistics; RAND prescription drug pricing',
            'Bill Sec 405 (IRP, DVB, 110% cap)',
        ],
        assumed: {
            internationalPricePctOfUS: '40% (conservative vs some OECD)',
            enforceIRP: 'user/slider; default full',
        },
        empirical: {
            internationalMedianPctOfUs: '~35% (OECD/RAND range)',
        },
        errorMetric: '~14% relative error on international anchor vs median',
        citation: 'OECD; RAND; bill Sec 405.',
    },
    admin: {
        label: 'Administrative overhead (schedule, floor)',
        sources: [
            'Woolhandler & Himmelstein (NEJM); CMS NHE admin share',
            'policyMath.getEffectiveAdminOverheadRate',
        ],
        assumed: {
            scheduleTarget: '2% (Medicare-like)',
            adminEfficiencyFloor: '4.5% (multipayer realism)',
        },
        empirical: {
            medicareAdmin: '~2%',
            privateInsurerAdmin: '~12%',
            blendedUS: '~15%',
        },
        errorMetric: 'N/A (floor is policy assumption, not fit)',
        citation: 'Woolhandler & Himmelstein; CMS NHE.',
    },
    trust_fund: {
        label: 'Trust fund (Sec 206 reserve band, auto-adjust)',
        sources: [
            'Bill Sec 206(d)(e), 207; fiscalCalculator.js',
            'CBO long-term trust fund conventions',
        ],
        assumed: {
            reserveTargetMin: '5%',
            reserveTargetMax: '10%',
            generalFundPhaseDown: 'as written',
        },
        empirical: {
            note: 'No US trust fund with same rules; structure follows bill.',
        },
        errorMetric: 'N/A',
        citation: 'Bill Sec 206, 207; CBO.',
    },
    compounding: {
        label: 'Compounding (GDP, medical inflation, discount)',
        sources: [
            'CBO Long-Term Economic Outlook',
            'nhe_baseline.json, federal_debt_baseline.json',
        ],
        assumed: {
            gdpGrowth: 'from baseline JSON',
            medicalInflation: 'embedded in Medicare cost growth',
        },
        empirical: {
            note: 'Baseline series aligned with CBO-style projections.',
        },
        errorMetric: 'N/A (baseline is exogenous input)',
        citation: 'CBO; CMS NHE; OMB.',
    },
};

/**
 * Build the full calibration/error report for telemetry and UI.
 * Includes backtest comparisons and per-module entries.
 */
export function buildCalibrationReport() {
    const backtestComparisons = getBacktestComparisons();
    const modules = CALIBRATION_MODULES.map(id => ({
        id,
        ...MODULE_CALIBRATION[id],
    }));

    return {
        generatedAt: new Date().toISOString(),
        backtestComparisons,
        modules,
        summary: {
            modulesWithErrorMetric: modules.filter(m => m.errorMetric && m.errorMetric !== 'N/A').length,
            totalModules: modules.length,
        },
    };
}
